import { Avatar, Chip, ClickAwayListener, List, ListItemButton, ListItemIcon, ListItemText, Paper, Popper } from '@mui/material';

// material-ui
import { useTheme } from '@mui/material/styles';

// assets
import {
    IconCalendar,
    IconCategory,
    IconLogout,
    IconSettings,
    IconUsers,
    IconTie,
    IconMapPin,
    IconNotebook,
    IconLayoutGridAdd,
    IconStar,
    IconBell,
    IconPlug
} from '@tabler/icons';
import User1 from 'assets/images/users/user-round.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import Transitions from 'shared/components/ui-component/extended/Transitions';
import { isPoaUiEnabled } from 'shared/utils/featureFlags';
import { canAccessPoSoTracking } from 'shared/utils/poSoTrackingAccess';
import { APPLICATION_ROUTES } from 'constants/routes';

/** Settings menu row hover / active — pale cream (reference) */
const MENU_HIGHLIGHT_BG = '#FDF2E9';

// ==============================|| PROFILE MENU ||============================== //

function pathMatches(pathname: string, prefix: string): boolean {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Detail routes use singular segments (`/user/:id`, `/location/:id`). */
function isUsersSection(pathname: string): boolean {
    return pathMatches(pathname, APPLICATION_ROUTES.users.list) || pathname.startsWith('/user/');
}

function isLocationsSection(pathname: string): boolean {
    return pathMatches(pathname, APPLICATION_ROUTES.locations.list) || pathname.startsWith('/location/');
}

const ProfileSection = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { logout, isOwner, isAdmin, currentUser } = useAuth();
    const isAdminOrOwner = isOwner || isAdmin;
    const poSoTrackingAllowed = canAccessPoSoTracking(currentUser?.sub);

    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef<any>(null);
    const handleLogout = async () => {
        try {
            logout();
        } catch (err) {
            console.error(err);
        }
    };

    const handleRedirect = (url: string) => {
        navigate(url);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current && !open) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const accent = theme.palette.secondary.main;

    const menuItemSx = {
        borderRadius: '10px',
        py: 1.25,
        px: 1.5,
        mb: 0.25,
        color: accent,
        transition: 'background-color 0.15s ease',
        '& .MuiListItemIcon-root': {
            minWidth: 40,
            color: accent
        },
        '& .MuiListItemText-primary': {
            color: accent,
            fontWeight: 500,
            fontSize: 14
        },
        '&:hover': {
            bgcolor: MENU_HIGHLIGHT_BG
        },
        '&.Mui-selected': {
            bgcolor: MENU_HIGHLIGHT_BG,
            '&:hover': { bgcolor: MENU_HIGHLIGHT_BG }
        }
    };

    return (
        <>
            <Chip
                ref={anchorRef}
                sx={{
                    height: 48,
                    alignItems: 'center',
                    borderRadius: '24px',
                    border: 'none',
                    bgcolor: '#2C2C2C',
                    transition: 'background-color 0.15s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: '#3D3D3D'
                    },
                    '&[aria-expanded="true"]': {
                        bgcolor: '#3D3D3D'
                    },
                    '& .MuiChip-icon': {
                        marginRight: '12px'
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        pr: 2,
                        pl: 2
                    }
                }}
                icon={
                    <Avatar
                        src={User1}
                        alt=""
                        sx={{
                            ...theme.typography.mediumAvatar,
                            width: 36,
                            height: 36,
                            margin: '6px 0 6px 6px !important',
                            cursor: 'pointer'
                        }}
                        aria-hidden
                    />
                }
                label={<IconSettings stroke={1.5} size={22} color="#FFFFFF" />}
                aria-controls={open ? 'profile-settings-menu' : undefined}
                aria-expanded={open}
                aria-haspopup="true"
                onClick={handleToggle}
                aria-label="Settings menu"
            />

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper
                                id="profile-settings-menu"
                                elevation={0}
                                sx={{
                                    minWidth: 280,
                                    maxWidth: 320,
                                    mt: 0.5,
                                    borderRadius: '12px',
                                    border: '1px solid #E8E8E8',
                                    bgcolor: '#fff',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                    overflow: 'hidden'
                                }}
                            >
                                {open && (
                                    <List
                                        component="nav"
                                        dense
                                        sx={{
                                            py: 1,
                                            px: 1.25,
                                            width: '100%',
                                            [theme.breakpoints.down('md')]: {
                                                minWidth: 'min(100vw - 32px, 320px)'
                                            }
                                        }}
                                    >
                                        {isOwner && (
                                            <ListItemButton
                                                sx={menuItemSx}
                                                selected={pathMatches(pathname, APPLICATION_ROUTES.franchisePartners.list)}
                                                onClick={() => handleRedirect(APPLICATION_ROUTES.franchisePartners.list)}
                                            >
                                                <ListItemIcon>
                                                    <IconTie stroke={1.5} size={20} color="currentColor" />
                                                </ListItemIcon>
                                                <ListItemText primary={<FormattedMessage id="Franchise Partners" />} disableTypography />
                                            </ListItemButton>
                                        )}
                                        {isAdminOrOwner && (
                                            <>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={isUsersSection(pathname)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.users.list)}
                                                >
                                                    <ListItemIcon>
                                                        <IconUsers stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<FormattedMessage id="users" />} disableTypography />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={isLocationsSection(pathname)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.locations.list)}
                                                >
                                                    <ListItemIcon>
                                                        <IconMapPin stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<FormattedMessage id="Locations" />} disableTypography />
                                                </ListItemButton>
                                            </>
                                        )}
                                        {isOwner && (
                                            <>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.products)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.products)}
                                                >
                                                    <ListItemIcon>
                                                        <IconNotebook stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<FormattedMessage id="Ingredients" />} disableTypography />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.locationGroupSettings)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.locationGroupSettings)}
                                                >
                                                    <ListItemIcon>
                                                        <IconLayoutGridAdd stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<FormattedMessage id="Config" />} disableTypography />
                                                </ListItemButton>
                                                {isPoaUiEnabled() && poSoTrackingAllowed && (
                                                    <ListItemButton
                                                        sx={menuItemSx}
                                                        selected={pathMatches(pathname, APPLICATION_ROUTES.integrations)}
                                                        onClick={() => handleRedirect(APPLICATION_ROUTES.integrations)}
                                                    >
                                                        <ListItemIcon>
                                                            <IconPlug stroke={1.5} size={20} color="currentColor" />
                                                        </ListItemIcon>
                                                        <ListItemText primary="Integrations" disableTypography />
                                                    </ListItemButton>
                                                )}
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.coreProducts)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.coreProducts)}
                                                >
                                                    <ListItemIcon>
                                                        <IconStar stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Core Products" disableTypography />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.deliveryScheduleSettings)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.deliveryScheduleSettings)}
                                                >
                                                    <ListItemIcon>
                                                        <IconCalendar stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Delivery Schedules" disableTypography />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.announcements)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.announcements)}
                                                >
                                                    <ListItemIcon>
                                                        <IconBell stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Announcements" disableTypography />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={menuItemSx}
                                                    selected={pathMatches(pathname, APPLICATION_ROUTES.categoryManagement)}
                                                    onClick={() => handleRedirect(APPLICATION_ROUTES.categoryManagement)}
                                                >
                                                    <ListItemIcon>
                                                        <IconCategory stroke={1.5} size={20} color="currentColor" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Categories" disableTypography />
                                                </ListItemButton>
                                            </>
                                        )}
                                        <ListItemButton sx={menuItemSx} selected={false} onClick={handleLogout}>
                                            <ListItemIcon>
                                                <IconLogout stroke={1.5} size={20} color="currentColor" />
                                            </ListItemIcon>
                                            <ListItemText primary={<FormattedMessage id="logout" />} disableTypography />
                                        </ListItemButton>
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
